import React, { useEffect } from 'react';
import { FiDownload, FiX, FiShare2, FiCopy, FiLinkedin } from 'react-icons/fi';
import { FaXTwitter, FaInstagram, FaThreads, FaFacebook } from 'react-icons/fa6';
import { PiButterfly } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import toast from '@/components/Toast/Toast';

interface ShareBadgeProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  badgeImage: string;
  badgeName?: string;
}

const ShareBadge: React.FC<ShareBadgeProps> = ({
  isOpen,
  onClose,
  onDownload,
  badgeImage,
  badgeName
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleModalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const copyToClipboard = async (platform?: string) => {
    try {
      const shareText = t('badges.share.shareText', { badgeName });
      const imageBlob = await fetch(badgeImage).then(r => r.blob());
      const shareData = [
        new ClipboardItem({
          [imageBlob.type]: imageBlob,
          'text/plain': new Blob([shareText], { type: 'text/plain' })
        })
      ];
      
      await navigator.clipboard.write(shareData);
      
      if (platform) {
        toast({
          title: t('badges.share.copied'),
          description: t('badges.share.imageAndTextCopied', { platform }),
          status: 'success'
        });
      } else {
        toast({
          title: t('badges.share.copied'),
          description: t('badges.share.readyToShare'),
          status: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: t('common.error'),
        description: t('badges.share.copyError'),
        status: 'error'
      });
    }
  };

  const handleLinkedInCertificate = async () => {
    try {
      await onDownload();
      const certUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent(badgeName || '')}&organizationName=Programe.tech&issueYear=${new Date().getFullYear()}&issueMonth=${new Date().getMonth() + 1}`;
      window.open(certUrl, '_blank');
      
      toast({
        title: t('badges.share.readyToShare'),
        description: t('badges.share.imageDownloadedTextCopied', { platform: 'LinkedIn' }),
        status: 'success'
      });
    } catch (error) {
      console.error('Failed to prepare LinkedIn certificate:', error);
      toast({
        title: t('common.error'),
        description: t('badges.share.error'),
        status: 'error'
      });
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black/50"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className="bg-base-100 rounded-lg p-3 w-full max-w-[280px]"
        onClick={handleModalClick}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-bold">
            {t('badges.share.title')}
          </h3>
          <button 
            className="btn btn-ghost btn-sm btn-circle"
            onClick={onClose}
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex justify-center">
            <img 
              src={badgeImage} 
              alt={badgeName} 
              className="w-24 h-24 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <button
              className="btn btn-primary h-auto min-h-[2.5rem] gap-2 py-2"
              onClick={() => onDownload()}
            >
              <FiDownload className="w-4 h-4" />
              {t('badges.share.download')}
            </button>

            <button
              className="btn btn-outline h-auto min-h-[2.5rem] gap-2 py-2"
              onClick={() => copyToClipboard()}
            >
              <FiCopy className="w-4 h-4" />
              {t('badges.share.copyToClipboard')}
            </button>
          </div>

          <div className="divider my-0">{t('badges.share.shareOn')}</div>

          <div className="grid grid-cols-3 gap-2">
            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={() => copyToClipboard('X')}
              title="Share on X"
            >
              <FaXTwitter className="w-4 h-4" />
            </button>

            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={() => copyToClipboard('Instagram')}
              title="Share on Instagram"
            >
              <FaInstagram className="w-4 h-4" />
            </button>

            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={() => copyToClipboard('Threads')}
              title="Share on Threads"
            >
              <FaThreads className="w-4 h-4" />
            </button>

            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={() => copyToClipboard('Facebook')}
              title="Share on Facebook"
            >
              <FaFacebook className="w-4 h-4" />
            </button>

            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={() => copyToClipboard('Bluesky')}
              title="Share on Bluesky"
            >
              <PiButterfly className="w-4 h-4" />
            </button>

            <button
              className="btn btn-outline btn-sm btn-square"
              onClick={handleLinkedInCertificate}
              title="Share on LinkedIn"
            >
              <FiLinkedin className="w-4 h-4" />
            </button>
          </div>

          <button
            className="btn btn-outline h-auto min-h-[3rem] py-2 w-full normal-case flex flex-col gap-1"
            onClick={handleLinkedInCertificate}
          >
            <div className="flex items-center gap-2">
              <FiLinkedin className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm whitespace-normal leading-tight">
                {t('badges.share.addToLinkedin')}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareBadge;